/**
 * Map pins: the one pin language every map speaks (Kurtis, 2026-10-02; the
 * LYF Map Pin Lab, plan in docs/plans/MAP_PINS.md).
 *
 * 1. Colour answers the map's ONE question. Trip maps: which day (DAY_COLORS
 *    in mapStyles.js). Discovery maps: what kind of place (PLACE_FAMILIES).
 *    Tours: the route (one colour). Curated walks: a dotted ink line (WALK_LINE)
 *    — the pattern marks "on foot", so walks add no colour. Nothing else gets
 *    a colour.
 * 2. Shape says what a thing is, on every map. Places are round discs; where
 *    you sleep is an ink square; trains and flights are slate squares;
 *    landmarks are a gold star disc.
 * 3. States are marks, not colours. Next up: a coral ring. Done: faded (the
 *    caller sets opacity). Saved: a small harbour bookmark badge. Selected:
 *    bigger, with a dark ring.
 *
 * Works for Google AdvancedMarkerElement content and MapLibre DOM markers
 * alike: the builder returns a plain <svg>.
 */

import { colors } from '../styles/colors';

const SVG_NS = 'http://www.w3.org/2000/svg';

export const PIN_COLORS = {
  ink: colors.surface.dark,        // #1e2632 — stays, city chain, board letters
  slate: colors.category.default,  // #5f6b7a — transport, saved-not-planned glyphs
  gold: colors.accent.dark,        // #d69e2e — landmarks ("recognition")
  coral: colors.coral.DEFAULT,     // #ff7a59 — next up
  harbour: colors.primary.DEFAULT, // #1f3fa3 — saved badge, tours, the picked board
  selected: colors.surface.dark,   // ring round the selected pin
  white: '#ffffff',
};

// Curated walks (Kurtis, 2026-10-22): dots in ink with a white edge. Green read
// as "park"; any new hue sat too close to a day colour.
export const WALK_LINE = PIN_COLORS.ink;

/**
 * Six place families instead of 19 hues: the glyph says café or bakery, the
 * colour says "food". Each carries a white glyph at 3:1 or better.
 */
export const PLACE_FAMILIES = {
  food: '#b8620f',
  culture: '#0f7f8a',
  outdoors: '#2f8a55',
  shows: '#7b4bb3',     // nightlife & shows
  shopping: '#b8456f',
  other: '#5f6b7a',
};

// Category keys (categoryUtils' display categories AND raw Google types) →
// family. 'stay' and 'landmark' are shapes of their own, not families.
const CATEGORY_KIND = {
  // Eat & drink
  restaurant: 'food', food: 'food', meal_takeaway: 'food', cafe: 'food', coffee: 'food',
  coffee_shop: 'food', bakery: 'food',
  // Nightlife & shows
  bar: 'shows', pub: 'shows', wine_bar: 'shows', night_club: 'shows', nightlife: 'shows',
  entertainment: 'shows', movie_theater: 'shows', cinema: 'shows', theater: 'shows',
  performing_arts_theater: 'shows', concert: 'shows', music: 'shows', comedy: 'shows',
  event: 'shows', amusement_park: 'shows', amusement_center: 'shows', casino: 'shows',
  bowling_alley: 'shows', karaoke: 'shows', video_arcade: 'shows', escape_room: 'shows',
  // Culture
  museum: 'culture', culture: 'culture', art_gallery: 'culture', arts: 'culture',
  gallery: 'culture', cultural_center: 'culture', historical: 'culture', library: 'culture',
  education: 'culture', school: 'culture', university: 'culture',
  // Outdoors
  park: 'outdoors', outdoor: 'outdoors', nature: 'outdoors', national_park: 'outdoors',
  hiking: 'outdoors', hiking_area: 'outdoors', campground: 'outdoors', garden: 'outdoors',
  beach: 'outdoors', zoo: 'outdoors', aquarium: 'outdoors', playground: 'outdoors',
  sports: 'outdoors', sports_complex: 'outdoors', gym: 'outdoors', fitness_center: 'outdoors',
  swimming: 'outdoors', golf: 'outdoors', adventure_sports_center: 'outdoors',
  // Shopping
  shopping: 'shopping', shopping_mall: 'shopping', store: 'shopping', mall: 'shopping',
  market: 'shopping', clothing_store: 'shopping', book_store: 'shopping',
  electronics_store: 'shopping',
  // Google subtypes the suffix rules below don't catch
  diner: 'food', steak_house: 'food', food_court: 'food', bar_and_grill: 'food',
  snack_bar: 'food', candy_store: 'food', juice_bar: 'food', cafeteria: 'food', deli: 'food',
  confectionery: 'food', golf_club: 'outdoors',
  sports_club: 'outdoors', ski_resort: 'outdoors',
  rv_park: 'stay', mobile_home_park: 'stay', japanese_inn: 'stay', cottage: 'stay', farmstay: 'stay',
  camping_cabin: 'stay', private_guest_room: 'stay',
  brewpub: 'shows', brewery: 'shows', concert_hall: 'shows', live_music_venue: 'shows',
  event_venue: 'shows', stadium: 'shows', water_park: 'shows',
  planetarium: 'culture', church: 'culture', visitor_center: 'culture',
  nature_preserve: 'outdoors', botanical_garden: 'outdoors', golf_course: 'outdoors',
  ice_skating_rink: 'outdoors', indoor_playground: 'outdoors', scenic_spot: 'outdoors',
  // Shapes of their own
  lodging: 'stay', hotel: 'stay', accommodation: 'stay', motel: 'stay', hostel: 'stay',
  inn: 'stay', bed_and_breakfast: 'stay', guest_house: 'stay',
  tourist_attraction: 'landmark', attraction: 'landmark', attractions: 'landmark',
  sightseeing: 'landmark', landmark: 'landmark', historical_place: 'landmark',
  monument: 'landmark', castle: 'landmark', sculpture: 'landmark',
  observation_deck: 'landmark', bridge: 'landmark', plaza: 'landmark',
};

// Google's long tail of subtypes (italian_restaurant, art_museum, city_park,
// cocktail_bar…), by suffix, after the exact lookup. Order matters: food shops
// (ice_cream_shop, sandwich_shop) before the generic `_shop` → shopping. The
// exact table above catches the types a suffix would get wrong (snack_bar,
// ski_resort, sports_club, rv_park).
const SUFFIX_KIND = [
  ['_restaurant', 'food'], ['_cafe', 'food'], ['_bakery', 'food'],
  ['ice_cream_shop', 'food'], ['dessert_shop', 'food'], ['pastry_shop', 'food'],
  ['chocolate_shop', 'food'], ['coffee_shop', 'food'], ['juice_shop', 'food'], ['tea_house', 'food'],
  ['sandwich_shop', 'food'], ['donut_shop', 'food'], ['bagel_shop', 'food'], ['acai_shop', 'food'],
  ['noodle_shop', 'food'],
  ['_bar', 'shows'], ['_pub', 'shows'], ['_club', 'shows'], ['_theater', 'shows'],
  ['_museum', 'culture'], ['_gallery', 'culture'],
  ['_park', 'outdoors'], ['_garden', 'outdoors'], ['_beach', 'outdoors'],
  ['_store', 'shopping'], ['_shop', 'shopping'], ['_market', 'shopping'],
  ['_hotel', 'stay'], ['_resort', 'stay'],
  ['_landmark', 'landmark'],
];

/**
 * What kind of thing a category or Google type is: a family key, 'stay',
 * 'landmark', or null when unknown (the caller decides; usually 'other').
 */
export function kindOfCategory(category) {
  if (!category) return null;
  const key = String(category).toLowerCase().trim().replace(/\s+/g, '_');
  if (CATEGORY_KIND[key]) return CATEGORY_KIND[key];
  const hit = SUFFIX_KIND.find(([suffix]) => key.endsWith(suffix));
  return hit ? hit[1] : null;
}

/**
 * The kind of a place from its Google types array (first known type wins,
 * the way getCategoryMarkerIcon picks its glyph). Never null: 'other'.
 */
export function kindOfTypes(types) {
  for (const type of types || []) {
    const kind = kindOfCategory(type);
    if (kind) return kind;
  }
  return 'other';
}

/** The fill a kind wears on a discovery map. */
export function kindColor(kind) {
  if (kind === 'stay') return PIN_COLORS.ink;
  if (kind === 'landmark') return PIN_COLORS.gold;
  return PLACE_FAMILIES[kind] || PLACE_FAMILIES.other;
}

// ---------------------------------------------------------------------------
// Glyphs: trusted static SVG on a 24×24 canvas, drawn in white (`fill="white"`
// or `stroke="white"`). buildPinSvg recolours them for white-bodied pins.
// ---------------------------------------------------------------------------

export const PIN_GLYPHS = {
  bed: '<path d="M7 13v4h10v-4H7zm0-1h10V9a2 2 0 00-2-2h-6a2 2 0 00-2 2v3z" fill="white"/>',
  star: '<path d="M12 5.5l1.9 3.9 4.3.6-3.1 3 .7 4.3-3.8-2-3.8 2 .7-4.3-3.1-3 4.3-.6z" fill="white"/>',
  // Transport (Material paths, as MapTab drew them)
  flight: '<path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" fill="white" transform="translate(2.4 2.4) scale(.8)"/>',
  train: '<path d="M4 15.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V5c0-3.5-3.58-4-8-4s-8 .5-8 4v10.5zm8 1.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm6-7H6V5h12v5z" fill="white" transform="translate(2.4 2.4) scale(.8)"/>',
  bus: '<path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z" fill="white" transform="translate(2.4 2.4) scale(.8)"/>',
  ferry: '<path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l1.89-6.68c.08-.26.06-.54-.06-.78s-.34-.42-.6-.5L20 10.62V6c0-1.1-.9-2-2-2h-3V1H9v3H6c-1.1 0-2 .9-2 2v4.62l-1.29.42c-.26.08-.48.26-.6.5s-.15.52-.06.78L3.95 19zM6 6h12v3.97L12 8 6 9.97V6z" fill="white" transform="translate(2.4 2.4) scale(.8)"/>',
  // Small badges (also 24-grid, scaled down where used)
  bookmark: '<path d="M8 5h8v14l-4-3-4 3z" fill="white"/>',
  flag: '<path d="M8 20V5M8 5h8l-2 3.5 2 3.5H8" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>',
  stop: '<rect x="7" y="7" width="10" height="10" rx="1.5" fill="white"/>',
  eye: '<path d="M3.5 12s3.2-5.5 8.5-5.5 8.5 5.5 8.5 5.5-3.2 5.5-8.5 5.5S3.5 12 3.5 12z" fill="none" stroke="white" stroke-width="2.2"/><circle cx="12" cy="12" r="2.6" fill="white"/>',
  camera: '<rect x="4" y="8" width="16" height="11" rx="2" fill="none" stroke="white" stroke-width="2.2"/><circle cx="12" cy="13.5" r="3" fill="white"/><path d="M9 8l1.2-2.5h3.6L15 8" fill="none" stroke="white" stroke-width="2.2"/>',
  cup: '<path d="M5 9h11v3.5a5.5 5.5 0 01-11 0zM16 10h1.5a2.5 2.5 0 010 5H16" fill="none" stroke="white" stroke-width="2.2" stroke-linejoin="round"/>',
  transfer: '<path d="M6 9h12l-3-3M18 15H6l3 3" fill="none" stroke="white" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>',
  // A tour on a Discover map: start and end joined by a dotted path
  route: '<circle cx="6.5" cy="17.5" r="2.3" fill="white"/><circle cx="17.5" cy="6.5" r="2.3" fill="white"/><path d="M8.8 17.5h5.4a2.9 2.9 0 000-5.8H9.8a2.9 2.9 0 010-5.2h5.4" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-dasharray="0.1 3.4"/>',
};

/** Glyph for a transport mode; null for 'other' (a plain square). */
export function transportGlyph(mode) {
  return PIN_GLYPHS[mode] || null;
}

// Recolour a white glyph for a white-bodied pin (small saved pins, hollow
// optional stops). The glyph strings above are ours, so a literal swap is safe.
function tintGlyph(glyphSvg, color) {
  return glyphSvg.replace(/"white"/g, `"${color}"`);
}

function el(name, attrs) {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attrs).forEach(([k, v]) => { if (v != null) node.setAttribute(k, String(v)); });
  return node;
}

function glyphGroup(glyphSvg, { color = null, scale = 1, cx = 12, cy = 12 } = {}) {
  const g = el('g', { transform: `translate(${cx} ${cy}) scale(${scale}) translate(-12 -12)` });
  g.innerHTML = color ? tintGlyph(glyphSvg, color) : glyphSvg;
  return g;
}

/**
 * Build one pin.
 *
 * @param {object} opts
 * @param {'disc'|'square'|'small'|'hollow'} [opts.shape='disc']
 *   disc: coloured circle (places, stops). square: rounded square (stays in
 *   ink, transport in slate). small: little white pin with a slate glyph
 *   (saved but not planned). hollow: white circle, dashed coloured edge,
 *   coloured number (optional tour stops).
 * @param {number} [opts.size=28] - Rendered px of the body (the badge and
 *   rings draw outside it; the svg is overflow:visible)
 * @param {string} [opts.fill] - Body colour (disc/square), edge colour (hollow),
 *   glyph colour (small; defaults to slate)
 * @param {string|number} [opts.text] - Centred number (white; coloured on hollow)
 * @param {string} [opts.glyphSvg] - Trusted static 24-grid glyph markup
 * @param {boolean} [opts.dashed=false] - Dashed white edge (an inherited,
 *   approximate location)
 * @param {string} [opts.ring] - Colour of a ring outside the body (coral = next up)
 * @param {boolean} [opts.pulse=false] - Animate the ring (respects reduced motion)
 * @param {boolean} [opts.selected=false] - Dark selection ring
 * @param {{glyph: string, fill?: string}} [opts.badge] - Small corner badge
 *   (saved: {glyph: 'bookmark'}; tour stop types: flag/stop/eye/camera/cup)
 * @param {string|number} [opts.count] - A number in a white corner badge in
 *   the body's colour (a Discover tour's stop count). Takes the badge's corner.
 * @returns {SVGSVGElement}
 */
export function buildPinSvg({
  shape = 'disc', size = 28, fill, text, glyphSvg, dashed = false,
  ring = null, pulse = false, selected = false, badge = null, count = null,
} = {}) {
  const svg = el('svg', { width: size, height: size, viewBox: '0 0 24 24' });
  svg.style.overflow = 'visible';
  svg.style.display = 'block';
  svg.dataset.pinShape = shape;

  if (selected) svg.appendChild(el('circle', { cx: 12, cy: 12, r: 14.5, fill: 'none', stroke: PIN_COLORS.selected, 'stroke-width': 2 }));
  if (ring) {
    if (pulse) svg.appendChild(el('circle', { cx: 12, cy: 12, r: 14.5, fill: 'none', stroke: ring, 'stroke-width': 3, class: 'lyf-pin-pulse' }));
    svg.appendChild(el('circle', { cx: 12, cy: 12, r: 14.5, fill: 'none', stroke: ring, 'stroke-width': 3 }));
  }

  const edge = dashed ? { 'stroke-dasharray': '3 2' } : {};
  if (shape === 'square') {
    svg.appendChild(el('rect', { x: 1, y: 1, width: 22, height: 22, rx: 6, fill, stroke: 'white', 'stroke-width': 2, 'data-pin-body': '', ...edge }));
  } else if (shape === 'small') {
    svg.appendChild(el('circle', { cx: 12, cy: 12, r: 9, fill: 'white', stroke: fill || PIN_COLORS.slate, 'stroke-width': 1.6, 'data-pin-body': '' }));
  } else if (shape === 'hollow') {
    svg.appendChild(el('circle', { cx: 12, cy: 12, r: 11, fill: 'white', stroke: fill, 'stroke-width': 2, 'stroke-dasharray': '3 2.4', 'data-pin-body': '' }));
  } else {
    svg.appendChild(el('circle', { cx: 12, cy: 12, r: 11, fill, stroke: 'white', 'stroke-width': 2, 'data-pin-body': '', ...edge }));
  }

  if (text != null && text !== '') {
    const label = String(text);
    const t = el('text', {
      x: 12, y: 16, 'text-anchor': 'middle',
      fill: shape === 'hollow' ? fill : 'white',
      'font-size': label.length > 2 ? 8.5 : 10.5, 'font-weight': 700,
      'font-family': 'system-ui, -apple-system, sans-serif',
    });
    t.textContent = label;
    svg.appendChild(t);
  } else if (glyphSvg) {
    if (shape === 'small') svg.appendChild(glyphGroup(glyphSvg, { color: fill || PIN_COLORS.slate, scale: 0.72 }));
    else if (shape === 'hollow') svg.appendChild(glyphGroup(glyphSvg, { color: fill }));
    else svg.appendChild(glyphGroup(glyphSvg));
  }

  if (count != null && count !== '') {
    const label = String(count);
    const bx = 21, by = 3, w = label.length > 1 ? 14 : 12;
    svg.appendChild(el('rect', { x: bx - w / 2, y: by - 6, width: w, height: 12, rx: 6, fill: 'white', stroke: fill, 'stroke-width': 1.2, 'data-pin-count': label }));
    const t = el('text', {
      x: bx, y: by + 3.2, 'text-anchor': 'middle', fill,
      'font-size': 8.5, 'font-weight': 800, 'font-family': 'system-ui, -apple-system, sans-serif',
    });
    t.textContent = label;
    svg.appendChild(t);
  } else if (badge && PIN_GLYPHS[badge.glyph]) {
    const bx = 20.5, by = 3.5;
    svg.appendChild(el('circle', { cx: bx, cy: by, r: 6, fill: badge.fill || PIN_COLORS.harbour, stroke: 'white', 'stroke-width': 1.5, 'data-pin-badge': badge.glyph }));
    svg.appendChild(glyphGroup(PIN_GLYPHS[badge.glyph], { scale: 0.42, cx: bx, cy: by }));
  }
  return svg;
}

/** The saved mark, ready to pass as `badge`. */
export const SAVED_BADGE = { glyph: 'bookmark', fill: PIN_COLORS.harbour };
